import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BarcodeDataResponse, 
  primaryButtonSx, 
  tableInputSubLabelSx,
  handleQuantityKeyDown,
  sanitizeQuantityInput,
  handleQuantityBlur,
  isPrintQuantityInvalid,
  getPrintQuantityHelperText,
  PrintQRBatchKeySx,
  PrintQRItemKeySx
} from '../Common/inventory.helper';

import {
  Box,
  Typography,
  Modal,
  IconButton,
  Button,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';

import { ReactComponent as CloseIcon } from '../../../assets/SVG/close-circle-greay.svg';
import { usePrinter } from '../../../hooks/usePrinter';
import { useAlert } from '../../../components/alertProvider';


interface QRPreviewProps {
  open: boolean;
  onClose: () => void;
  QRResults: BarcodeDataResponse[];
  // onPrint: (item: BarcodeDataResponse, quantity: number) => void;
  // onThermalPrint?: (item: BarcodeDataResponse, quantity: number) => void;
  // isThermalPrinterReady?: boolean;
}

const headerSx = {
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontSize: '20px',
  color: '#232323',
};

const boxContainerSx = {
  width: 600,
  bgcolor: 'background.paper',
  borderRadius: 4,
  mx: 'auto',
  mt: '15%',
  boxShadow: 24,
  outline: 'none',
  overflow: 'hidden',
};

const closeIconSx = {
  color: '#9E9E9E',
  '&:hover': {
    backgroundColor: 'rgba(158, 158, 158, 0.1)',
  },
};

const imageBox = { 
  maxWidth: 180, 
  minHeight: 80, 
  border: '1px solid #ccc', 
  background: '#fff', 
  padding: 1, 
  borderRadius: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};


const StockQRPreview: React.FC<QRPreviewProps> = ({
  open,
  onClose,
  QRResults,
  // onPrint,
  // onThermalPrint,
  // isThermalPrinterReady = false,
}) => {
  const { t: tStock } = useTranslation('stock_config');
  const { addAlert } = useAlert();
  const { 
    isConnected: isPrinterConnected, 
    printers, 
    getPrinters, 
    printImageFromUrl
  } = usePrinter();

  // Selected printer for label printing
  const [selectedPrinter, setSelectedPrinter] = useState<string>('');

  // Handle printer selection
  const handlePrinterChange = (event: SelectChangeEvent<string>) => {
    setSelectedPrinter(event.target.value);
  };

  const [quantities, setQuantities] = React.useState<{ [key: string]: number | string }>({});

  // Initialize quantities when QRResults change
  React.useEffect(() => {
    const initialQuantities: { [key: string]: number } = {};
    QRResults.forEach(item => {
      initialQuantities[item.batch_id] = 1; // Default quantity
    });
    setQuantities(initialQuantities);
  }, [QRResults]);


  const isPrintButtonDisabled = (qty: number | string | undefined): boolean => {
    if (qty === undefined) return false;
    if (qty === '' || (typeof qty === 'number' && qty < 1)) return true;
    return false;
  };

  
  // Get selected printer info
  const getSelectedPrinterInfo = () => {
    const printer = printers.find(p => p.name === selectedPrinter);
    return printer ? { name: printer.name, port: Number(printer.port) } : null;
  };


  // New thermal label printer function
  const handleThermalPrint = async (item: BarcodeDataResponse, quantity = 1) => {
    const printerInfo = getSelectedPrinterInfo();
      
    if (!isPrinterConnected) {
      addAlert(tStock('Printer_Not_Connected'), 'error');
      return;
    }
  
    if (!printerInfo) {
      addAlert(tStock('Select_Printer_First'), 'error');
      return;
    }
  
    try {
      await printImageFromUrl(
        printerInfo.name,
        printerInfo.port,
        item.proxy_image_url, // Use the proxied URL for printing
        {
          width: 36.5,        // Label canvas width in mm
          height: 9.5,        // Label canvas height in mm
          imageWidth: 25,     // Barcode image width in mm
          imageHeight: 8.5,   // Barcode image height in mm
          x: 0,             // X offset from left edge
          y: 1,             // Y offset from top edge
          quantity: quantity,
        }
      );
         
      addAlert(tStock('Print_Success'), 'success'); 
    } catch {
      addAlert(tStock('Print_Error'), 'error');
    }
  };
  



  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={boxContainerSx}>

        {/* Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
          p={1}
          px={2}
          mb={2}
          borderBottom="1px solid rgba(226, 227, 229, 1)"
        >
          <Typography sx={headerSx}>{tStock('QR_Preview_Print_Header')}</Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={getPrinters}
                disabled={!isPrinterConnected}
              >
                {isPrinterConnected ? tStock('Find_Printers') : tStock('Printer_Not_Connected')}
              </Button>
                    
              {printers.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="printer-select-label">{tStock('Select_Printer')}</InputLabel>
                  <Select
                    labelId="printer-select-label"
                    value={selectedPrinter}
                    label={tStock('Select_Printer')}
                    onChange={handlePrinterChange}
                  >
                    {printers.map((printer) => (
                      <MenuItem key={printer.name} value={printer.name}>
                        {printer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
                          

            <Tooltip title={tStock('Close')} arrow>
              <IconButton onClick={onClose} sx={closeIconSx}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Body */}
        <Box p={2} pt={0}>
          <Typography sx={PrintQRBatchKeySx}>
            {tStock('QR_Review_Description')}
          </Typography>

          <Typography sx={PrintQRBatchKeySx} mb={2}>
            {tStock('QR_Adjust_Quantities')}
          </Typography>

          {QRResults.length === 0 ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center"
              minHeight={200}
              borderBottom="1px solid rgba(226, 227, 229, 1)"
            >
              <Typography variant="body1" color="text.secondary">
                {tStock('QR_No_Data_Available')}
              </Typography>
            </Box>
          ) : (
            <Box maxHeight={560} overflow="auto" pr={1}>
              
              {QRResults.map((item) => (
                <Box 
                  key={item.batch_id}
                  mb={2}
                  p={2}
                  border="1px solid #eee"
                  borderRadius={4}
                  display="flex"
                  alignItems="center"
                  gap={3}
                >
                  {/* QR Image */}
                  <Box sx={imageBox}>
                    <img 
                      src={item.barcode_image_url}
                      alt={`QR for ${item.item_name} - ${item.batch_code}`}
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                  </Box>

                  {/* Details */}
                  <Box flex={1}>
                    <Typography sx={PrintQRItemKeySx} mb={0.5}>
                      {item.item_name}
                    </Typography>

                    <Typography sx={PrintQRBatchKeySx} mb={1}>
                      {tStock('Label_Batch')}: {item.batch_code}
                    </Typography>

                    {/* Quantity Field */}
                    <Box mt={2} maxWidth={250}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={tStock('Label_Quantity')}
                        value={quantities[item.batch_id] ?? 1}
                        inputProps={{
                          min: 1,
                          max: 9999,
                        }}
                        onKeyDown={handleQuantityKeyDown}
                        onChange={(e) => {
                          const sanitized = sanitizeQuantityInput(e.target.value);
                          setQuantities(prev => ({ ...prev, [item.batch_id]: sanitized }));
                        }}
                        onBlur={() => {
                          const validQty = handleQuantityBlur(quantities[item.batch_id]);
                          setQuantities(prev => ({ ...prev, [item.batch_id]: validQty }));
                        }}
                        error={isPrintQuantityInvalid(quantities[item.batch_id])}
                        helperText={getPrintQuantityHelperText(quantities[item.batch_id], tStock('Quantity_Required'))}
                        sx={tableInputSubLabelSx}
                      />
                    </Box>

                    {/* Print Button */}
                    <Box display="flex" gap={1} mt={2}>
                      {/* <Button
                        variant="contained"
                        disabled={isPrintButtonDisabled(quantities[item.batch_id])}
                        sx={{ ...primaryButtonSx, flex: 1 }}
                        onClick={() => {
                          const qty = quantities[item.batch_id];
                          const finalQty =
                            typeof qty === 'number' && qty > 0 ? qty : 1;

                          onPrint(item, finalQty);
                        }}
                      >
                        {tStock('Print_QR_Button', { quantity: Number(quantities[item.batch_id] || 1) })}
                      </Button> */}

                      {/* Thermal Print Button */}
                      {/* {onThermalPrint && ( */}
                      <Button
                        variant="contained"
                        disabled={isPrintButtonDisabled(quantities[item.batch_id]) || !selectedPrinter}
                        sx={{ ...primaryButtonSx, flex: 1 }}
                        onClick={() => {
                          const qty = quantities[item.batch_id];
                          const finalQty =
                              typeof qty === 'number' && qty > 0 ? qty : 1;

                          // onThermalPrint(item, finalQty);
                          handleThermalPrint(item, finalQty);
                        }}
                      >
                        {tStock('Print_QR_Button', { quantity: Number(quantities[item.batch_id] || 1) })}
                      </Button>
                        
                      {/* )} */}
                    </Box>

                  </Box>
                </Box>
              ))}

            </Box>
          )}
        </Box>

      </Box>
    </Modal>
  );
};

export default StockQRPreview;
